import React, { useState, useEffect, useRef, useContext } from "react";
import TemplateElementWarningsService from "packs/services/convertflow/template_element_warnings_service";
import SettingWarningLabel from "../shared/SettingWarningLabel";
import useActions from "../actions/useActions";
import ActionSettingsInput from "./ActionSettingsInput";
import CFSelect from "../shared/forms/CFSelect";
import useIntegrationRefresh from "../integrations/useIntegrationRefresh";
import { findIntegrationByService } from "../../utils/integrationFieldTypeMap";
import { findActionDefinition } from "../../utils/actionDefinitions";
import {
  websiteMeetsIntegrationPlanTier,
  integrationUpgradePlan,
} from "../../utils/integrationPlanAccess";
import { VariantContextData } from "../variants/VariantBuilderContext";

const ActionForm = (props) => {
  const form = useRef(null);
  const { currentActionsHook } = useActions(props);
  const { refreshIntegration } = useIntegrationRefresh();
  const contextValue = useContext(VariantContextData);
  const current_user = contextValue?.[0]?.objects?.current_user;
  const isBetaOrAdmin = !!(current_user?.admin_type || current_user?.beta);

  const buildFormData = (sourceAction) => ({
    contactable_type: props.contactable_type,
    contactable_id: props.contactable_id,
    element_id: props.element ? props.element.id : null,
    cta_id: props.cta ? props.cta.id : null,
    id: sourceAction ? sourceAction.id : null,
    action_type: sourceAction ? sourceAction.action_type : null,
    data: sourceAction ? { ...(sourceAction.data || {}) } : {},
  });

  const [formData, setFormData] = useState(buildFormData(props.action));
  const Actions = currentActionsHook;
  const action = props.action;
  const website = props.website;
  const websiteId = website?.id || props.cta?.website_id || action?.website_id;
  const accountId = website?.account_id || props.account?.id;
  const billableActionHref =
    websiteId && accountId
      ? `/accounts/${accountId}/subscriptions?website_id=${websiteId}`
      : null;
  const actionsList = props.actionsList;

  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedActionOption, setSelectedActionOption] = useState(
    props.action
      ? {
          value: action.action_type,
          label: action.action_type,
          tooltip: action.action_type,
        }
      : undefined
  );

  const [isActionOptionLoading, setIsActionOptionLoading] = useState(false);

  useEffect(() => {
    if (action) {
      setSelectedAction(findActionDefinition(actionsList, action.action_type));

      setSelectedActionOption({
        value: action.action_type,
        label: action.action_type,
        tooltip: action.action_type,
      });
    }
  }, [action, action && action.action_type, actionsList]);

  useEffect(() => {
    setFormData(buildFormData(action));
  }, [action]);

  useEffect(() => {
    const actionType = action?.action_type || formData?.action_type;
    if (!actionType || !website?.integrations) return;
    const integrationType = actionType.split(" - ")[0];
    if (!integrationType || integrationType === "ConvertFlow") return;
    const integration = findIntegrationByService(website, integrationType);
    if (integration) refreshIntegration(integration.id);
  }, [action?.action_type, formData?.action_type]);

  const actionSelectOptions = () => {
    let selectOptions = [
      { label: "Select an automation type", options: [], isDisabled: true },
    ];

    Object.values(actionsList).forEach((integration) => {
      let groupOption = { label: integration.integration_type, options: [] };

      Object.values(integration.action_types).forEach((action_type) => {
        const isHidden =
          action_type.hidden === true ||
          (action_type.beta === true && !isBetaOrAdmin);
        const isCreatingNew = !action;

        if (isCreatingNew && isHidden) {
          return; // Skip hidden action types when creating new actions
        }

        const isIntegrationAction =
          integration.integration_type !== "ConvertFlow";
        const isExplicitlyBillable = action_type.billable === true;
        const isExplicitlyNonBillable = action_type.billable === false;
        const isBillable = isExplicitlyNonBillable
          ? false
          : isIntegrationAction || isExplicitlyBillable;
        const shouldShowBillableIcon =
          isBillable && Boolean(billableActionHref);

        groupOption.options.push({
          label: action_type.action_type,
          value: action_type.action_type,
          icon: `/images/${integration.icon}`,
          billableActionHref: shouldShowBillableIcon
            ? billableActionHref
            : null,
          tooltip: action_type.action_type,
        });
      });

      // Only add the group if it has options
      if (groupOption.options.length > 0) {
        selectOptions.push(groupOption);
      }
    });

    return selectOptions;
  };

  const settingsToRender = (() => {
    if (!selectedAction || !selectedAction.settings) return [];
    if (props.dropdownOnly) return [];

    return selectedAction.stacked_layout
      ? selectedAction.settings.filter((s) => s.input_type === "formula")
      : selectedAction.settings;
  })();

  const firstInlineSetting = (() => {
    if (
      !props.dropdownOnly ||
      !action ||
      !selectedAction ||
      selectedAction.stacked_layout
    ) {
      return null;
    }

    return (selectedAction.settings || []).find(
      (setting) =>
        setting &&
        setting.input_type !== "hidden" &&
        setting.input_type !== "formula" &&
        setting.input_type !== "textarea"
    );
  })();

  const handleSubmit = (newFormData) => {
    const effectiveFormData = newFormData || formData;
    const resolvedAction =
      selectedAction ||
      findActionDefinition(actionsList, effectiveFormData.action_type);

    if (!resolvedAction) {
      return false;
    }

    const payload = {
      website_id: website.id,
      action_object: effectiveFormData,
    };

    if (action) {
      Actions.update(payload, (data) => {
        props.onUpdate(data);
      });
    } else {
      Actions.create(payload, (data) => {
        props.onCreate(data);
      });
    }
  };

  const loadShopifyProducts = () => {
    const shopifyIntegration = Object.values(website.integrations).filter(
      (i) => i.integration_type == "Shopify" && i.status == true
    )[0];

    const parsed_store_name = (
      shopifyIntegration?.api_data?.api_url ||
      shopifyIntegration?.api_data?.website
    )
      ?.replace("http://", "")
      ?.replace("https://", "")
      ?.replace(".myshopify.com", "")
      ?.replace("/", "")
      ?.replace(" ", "");

    const shopifyClient = new ShopifyGraphqlClient({
      store_name: parsed_store_name,
      token: shopifyIntegration?.api_data["storefront-token"],
    });

    if (
      window.cf_shopify_products_loading == true ||
      window.cf_shopify_products_loaded == true ||
      !shopifyIntegration
    )
      return;

    window.cf_shopify_products_loading = true;
    setIsActionOptionLoading(true);

    new ProductsAdapterService(shopifyClient, "ShopifyGraphql", true)
      .getProducts()
      .then((products) => {
        window.cf_shopify_products = products;
      })
      .finally(() => {
        setIsActionOptionLoading(false);
        window.cf_shopify_products_loaded = true;
        window.cf_shopify_products_loading = false;
      });
  };

  if (action && action.action_type.includes("Shopify")) {
    loadShopifyProducts();
  }

  const renderActionSetting = (setting, index) => {
    let integrationType =
      selectedAction && selectedAction.action_type.split(" -")[0];
    let integration = $.map(website.integrations, function (integration) {
      if (integration.integration_type == integrationType) return integration;
    })[0];

    return (
      <ActionSettingsInput
        action={action}
        key={`${setting.name}-${setting.label}-${index}`}
        type="automation"
        setting={setting}
        integration={integration}
        formData={formData}
        setFormData={setFormData}
        onFormulaCommit={(latestFormData) => handleSubmit(latestFormData)}
        isLoading={isActionOptionLoading}
        website={website}
        cta={props.cta}
        builder={props.builder}
        dropdownOnly={props.dropdownOnly}
        menuPortalZIndex={props.menuPortalZIndex}
        handleSubmit={handleSubmit}
        hideLabel={props.dropdownOnly}
        onChange={(e) => {
          let newFormData = formData;
          _.set(
            newFormData,
            `${
              !integration ? setting.attribute : `[data][${setting.attribute}]`
            }`,
            e.value || e.target.value
          );
          setFormData(newFormData);

          if (action && !props.builder && setting.input_type == "select") {
            handleSubmit(newFormData);
          }
        }}
        onDone={(e) => {
          if (action) {
            if (props.builder) {
              const value = e.value || e.target.value;
              dispatchCustomEvent("updateBuilder", {
                updates: [
                  {
                    object_type: "actions",
                    object_id: action.id,
                    setting_name: !integration
                      ? setting.attribute
                      : `[data][${setting.attribute}]`,
                    value: value,
                  },
                ],
              });
            } else if (setting.input_type !== "select") {
              handleSubmit();
            }
          }
        }}
      />
    );
  };

  return (
    <React.Fragment>
      <SettingWarningLabel
        warning={
          action && TemplateElementWarningsService.getObjectWarning(action)
        }
      />
      <div
        className={`w-full min-w-0 relative max-w-[648px]${
          props.dropdownOnly ? "" : " float-left"
        }`}
        data-attribute="action_type"
      >
        <form
          className={
            props.layout === "stacked"
              ? `flex flex-col gap-y-4 relative`
              : props.dropdownOnly
              ? `flex flex-col min-w-0 gap-y-2 relative ${
                  !action ? "pb-[10px]" : ""
                }`
              : `flex min-w-0 gap-x-3 relative ${!action ? "pb-[10px]" : ""}`
          }
          data-remote="true"
          ref={form}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <input name="builder" value="true" type="hidden" />
          <input
            name={`action_object[contactable_type]`}
            value={props.contactable_type}
            type="hidden"
          />
          <input
            name={`action_object[contactable_id]`}
            value={props.contactable_id}
            type="hidden"
          />

          {props.element && (
            <input
              name="action_object[element_id]"
              value={props.element.id}
              type="hidden"
            />
          )}

          {props.cta && (
            <input
              name="action_object[cta_id]"
              value={props.cta.id}
              type="hidden"
            />
          )}

          {action && (
            <input name="action_object[id]" value={action.id} type="hidden" />
          )}
          {(() => {
            const actionTypeSelect = (
              <CFSelect
                id="cf-select-automation_type"
                className="smart-select"
                value={selectedActionOption}
                menuPortalTarget={document.body}
                menuPortalZIndex={props.menuPortalZIndex}
                isSearchable={true}
                name="action_object[action_type]"
                options={actionSelectOptions()}
                onChange={(e) => {
                  const actionType = e.value;
                  const actionDefinition = findActionDefinition(
                    actionsList,
                    actionType
                  );
                  const integrationType =
                    actionDefinition?.integration_type ||
                    actionType.split(" - ")[0];
                  const integrationGroup =
                    integrationType &&
                    Object.values(actionsList).find(
                      (item) => item.integration_type === integrationType
                    );

                  if (
                    integrationGroup &&
                    integrationGroup.integration_type !== "ConvertFlow"
                  ) {
                    const planType =
                      website?.plan_type ||
                      window.plan_type ||
                      (typeof document !== "undefined"
                        ? document
                            .getElementById("upgrade")
                            ?.getAttribute("data-plan-type")
                        : null);
                    const websitePremium =
                      website?.premium !== undefined &&
                      website?.premium !== null
                        ? website.premium
                        : typeof document !== "undefined"
                        ? document
                            .getElementById("upgrade")
                            ?.getAttribute("data-website-premium")
                        : null;
                    const websitePlan =
                      website?.plan_type ||
                      (typeof document !== "undefined"
                        ? document
                            .getElementById("upgrade")
                            ?.getAttribute("data-website-plan")
                        : null) ||
                      planType;
                    const planTier =
                      actionDefinition?.plan_tier ||
                      integrationGroup.plan_tier ||
                      "pro";

                    if (
                      !websiteMeetsIntegrationPlanTier({
                        planType: websitePlan || planType,
                        websitePremium,
                        planTier,
                      })
                    ) {
                      if (
                        typeof window !== "undefined" &&
                        typeof window.subscriptions?.upgrade_modal ===
                          "function"
                      ) {
                        window.subscriptions.upgrade_modal(
                          "event",
                          false,
                          `use ${integrationGroup.integration_type} automations`,
                          integrationUpgradePlan(planTier),
                          `Triggered feature CTA – Integration Actions – ${integrationGroup.integration_type}`
                        );
                      }
                      return;
                    }
                  }

                  setSelectedActionOption({
                    value: actionType,
                    label: actionType,
                    tooltip: actionType,
                  });

                  setSelectedAction(actionDefinition);

                  let newFormData = formData;
                  newFormData.action_type = actionType;
                  setFormData(newFormData);

                  if (props.builder && action) {
                    dispatchCustomEvent("updateBuilder", {
                      updates: [
                        {
                          object_type: "actions",
                          object_id: action.id,
                          setting_name: "[action_type]",
                          value: actionType,
                        },
                      ],
                    });
                  }

                  if (actionType.includes("Shopify")) {
                    loadShopifyProducts();
                  }

                  if (!action && props.dropdownOnly) {
                    handleSubmit(newFormData); // create the new action
                  } else if (action && !props.builder) {
                    handleSubmit(newFormData); // persist type change outside the builder
                  }

                  const newTypeHasSettings = !!(
                    actionDefinition &&
                    Array.isArray(actionDefinition.settings) &&
                    actionDefinition.settings.length > 0
                  );

                  if (action && props.dropdownOnly && newTypeHasSettings) {
                    // Defer so updateBuilder re-renders don't drop the edit open state.
                    const actionId = action.id;
                    window.setTimeout(() => {
                      if (typeof props.onEdit === "function") {
                        props.onEdit({ ...action, action_type: actionType });
                      }
                      document.dispatchEvent(
                        new CustomEvent("openAutomationEdit", {
                          detail: { action_id: actionId },
                        })
                      );
                    }, 0);
                  }
                }}
              />
            );

            if (props.dropdownOnly) {
              return (
                <div className="flex min-w-0 w-full items-center gap-x-2">
                  <div
                    className={
                      firstInlineSetting
                        ? "min-w-0 flex-1 overflow-hidden"
                        : "w-full min-w-0 flex-grow"
                    }
                    title={
                      (selectedActionOption &&
                        (selectedActionOption.tooltip ||
                          selectedActionOption.label)) ||
                      undefined
                    }
                  >
                    {actionTypeSelect}
                  </div>
                  {firstInlineSetting ? (
                    <div className="min-w-0 flex-1 overflow-hidden">
                      {renderActionSetting(firstInlineSetting, 0)}
                    </div>
                  ) : null}
                  {props.inlineControls}
                </div>
              );
            }

            return (
              <div
                className={
                  props.layout === "stacked"
                    ? "w-full"
                    : "flex-grow min-w-[25%] max-w-[50%]"
                }
              >
                {actionTypeSelect}
              </div>
            );
          })()}

          {settingsToRender.length > 0 && (
            <React.Fragment>
              {settingsToRender.map((setting, index) =>
                renderActionSetting(setting, index)
              )}
            </React.Fragment>
          )}

          {!action && !props.dropdownOnly && (
            <button
              className={`w-[75px] border-0 border-transparent py-3 px-3 rounded-lg text-xl ml-1 mr-1 semibold bg-blue-600 text-white cursor-pointer float-right`}
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              Save
            </button>
          )}
        </form>
      </div>
    </React.Fragment>
  );
};

export default ActionForm;
